import { useState, useCallback } from 'react';
import s3Service from '../services/s3Service';

/**
 * Custom hook for handling file uploads to S3
 * @param {string} orgId - Organization ID for folder structure
 * @param {string} section - Section name for folder organization (e.g., 'documents', 'certificates')
 * @returns {object} - Upload utilities and state
 */
export const useFileUpload = (orgId = '', section = 'documents') => {
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadErrors, setUploadErrors] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [isUploading, setIsUploading] = useState(false);

  // Generate folder path for S3
  const generateFolderPath = useCallback((customSection = null) => {
    const folderSection = customSection || section;
    return `applications/${orgId}/${folderSection}/`;
  }, [orgId, section]);

  // Upload single file
  const uploadFile = useCallback(async (file, fieldName, customSection = null) => {
    if (!file) {
      return { success: false, error: 'No file provided' };
    }

    // Clear any previous state for this field
    setUploadProgress(prev => ({ ...prev, [fieldName]: 0 }));
    setUploadErrors(prev => ({ ...prev, [fieldName]: null }));
    setUploadedFiles(prev => {
      const updated = { ...prev };
      delete updated[fieldName];
      return updated;
    });

    setIsUploading(true);

    try {
      const folderPath = generateFolderPath(customSection);
      
      // Progress callback
      const onProgress = (percent) => {
        setUploadProgress(prev => ({ ...prev, [fieldName]: percent }));
      };

      // Upload to S3
      const result = await s3Service.uploadFile(file, folderPath, null, onProgress);

      if (result.success) {
        // Store uploaded file info
        setUploadedFiles(prev => ({
          ...prev,
          [fieldName]: {
            ...result.data,
            originalFile: file
          }
        }));
        
        setUploadProgress(prev => ({ ...prev, [fieldName]: 100 }));
        return result;
      } else {
        setUploadErrors(prev => ({ ...prev, [fieldName]: result.error }));
        return result;
      }
    } catch (error) {
      console.error('useFileUpload: Upload error:', error);
      const errorMessage = error.message || 'Upload failed';
      setUploadErrors(prev => ({ ...prev, [fieldName]: errorMessage }));
      return { success: false, error: errorMessage };
    } finally {
      setIsUploading(false);
    }
  }, [generateFolderPath]);

  // Upload multiple files
  const uploadMultipleFiles = useCallback(async (files, fieldName, customSection = null) => {
    if (!files || files.length === 0) {
      return { success: false, error: 'No files provided' };
    }

    setIsUploading(true);
    setUploadProgress(prev => ({ ...prev, [fieldName]: 0 }));
    setUploadErrors(prev => ({ ...prev, [fieldName]: null }));

    try {
      const folderPath = generateFolderPath(customSection);
      
      // Progress callback
      const onProgress = (overallPercent, fileIndex, filePercent) => {
        setUploadProgress(prev => ({ 
          ...prev, 
          [fieldName]: overallPercent,
          [`${fieldName}_file_${fileIndex}`]: filePercent
        }));
      };

      // Upload to S3
      const result = await s3Service.uploadMultipleFiles(files, folderPath, onProgress);

      if (result.success) {
        // Store uploaded files info
        setUploadedFiles(prev => ({
          ...prev,
          [fieldName]: result.data.successful.map(item => ({
            ...item.result.data,
            originalFile: files[item.index]
          }))
        }));
        
        setUploadProgress(prev => ({ ...prev, [fieldName]: 100 }));
        return result;
      } else {
        setUploadErrors(prev => ({ ...prev, [fieldName]: result.error }));
        return result;
      }
    } catch (error) {
      const errorMessage = error.message || 'Upload failed';
      setUploadErrors(prev => ({ ...prev, [fieldName]: errorMessage }));
      return { success: false, error: errorMessage };
    } finally {
      setIsUploading(false);
    }
  }, [generateFolderPath]);

  // Delete uploaded file
  const deleteFile = useCallback(async (fieldName) => {
    const fileInfo = uploadedFiles[fieldName];
    if (!fileInfo || !fileInfo.key) {
      return { success: false, error: 'No file to delete' };
    }

    try {
      const result = await s3Service.deleteFile(fileInfo.key);
      
      if (result.success) {
        // Remove from uploaded files
        setUploadedFiles(prev => {
          const updated = { ...prev };
          delete updated[fieldName];
          return updated;
        });
        
        // Clear progress and errors
        setUploadProgress(prev => {
          const updated = { ...prev };
          delete updated[fieldName];
          return updated;
        });
        
        setUploadErrors(prev => {
          const updated = { ...prev };
          delete updated[fieldName];
          return updated;
        });
      }
      
      return result;
    } catch (error) {
      return { success: false, error: error.message || 'Delete failed' };
    }
  }, [uploadedFiles]);

  // Get presigned URL for file access
  const getFileUrl = useCallback(async (fieldName, expiresIn = 3600) => {
    const fileInfo = uploadedFiles[fieldName];
    if (!fileInfo) {
      return { success: false, error: 'No file found' };
    }

    try {
      // Use the file key if available, otherwise use fileName
      let fileKey = fileInfo.key || fileInfo.fileName || fileInfo.originalName;
      
      if (!fileKey) {
        return { success: false, error: 'No file name or key found' };
      }

      // Handle different key formats:
      // 1. Full S3 key with 'live/' prefix: live/applications/67474/organization/filename.pdf
      // 2. Partial S3 key: applications/67474/organization/filename.pdf  
      // 3. Just filename: filename.pdf
      
      let processedKey;
      if (fileKey.includes('live/')) {
        // Already has the full S3 path including live/ prefix - use as is
        processedKey = fileKey;
      } else if (fileKey.includes('applications/')) {
        // Has applications path but missing live/ prefix - add it
        processedKey = `live/${fileKey}`;
      } else if (orgId) {
        // Just a filename - build full path
        const folderPath = generateFolderPath();
        processedKey = `live/${folderPath}${fileKey}`;
      } else {
        // Use fileKey as-is and let backend handle it
        processedKey = fileKey;
      }

      console.log('File key processing for uploaded file:', { original: fileKey, processed: processedKey });

      // Use the backend API to get presigned URL
      const result = await s3Service.getPresignedUrl(processedKey, expiresIn);
      
      if (result.success) {
        return { 
          success: true, 
          data: { url: result.data.url || result.data.presignedUrl || result.data },
          message: 'File URL generated successfully'
        };
      } else {
        return { success: false, error: result.error || 'Failed to generate file URL' };
      }
    } catch (error) {
      return { success: false, error: error.message || 'Failed to get file URL' };
    }
  }, [uploadedFiles, orgId, section, generateFolderPath]);

  // Clear all upload state
  const clearUploadState = useCallback(() => {
    setUploadProgress({});
    setUploadErrors({});
    setUploadedFiles({});
    setIsUploading(false);
  }, []);

  // Get upload info for a specific field
  const getUploadInfo = useCallback((fieldName) => {
    return {
      file: uploadedFiles[fieldName] || null,
      progress: uploadProgress[fieldName] || 0,
      error: uploadErrors[fieldName] || null,
      isUploading: isUploading && uploadProgress[fieldName] !== undefined
    };
  }, [uploadedFiles, uploadProgress, uploadErrors, isUploading]);

  // Check if any upload is in progress
  const hasActiveUploads = useCallback(() => {
    return isUploading || Object.values(uploadProgress).some(progress => 
      progress > 0 && progress < 100
    );
  }, [isUploading, uploadProgress]);

  // Get presigned URL for any file (not just uploaded ones)
  const getFileUrlByKey = useCallback(async (fileKey, expiresIn = 3600) => {
    if (!fileKey) {
      return { success: false, error: 'No file key provided' };
    }

    try {
      let processedKey = fileKey;
      
      // Handle different key formats:
      // 1. Full S3 key with 'live/' prefix: live/applications/67474/organization/filename.pdf
      // 2. Partial S3 key: applications/67474/organization/filename.pdf  
      // 3. Just filename: filename.pdf
      
      if (fileKey.includes('live/')) {
        // Already has the full S3 path including live/ prefix - use as is
        processedKey = fileKey;
      } else if (fileKey.includes('applications/')) {
        // Has applications path but missing live/ prefix - add it
        processedKey = `live/${fileKey}`;
      } else if (orgId) {
        // Just a filename - build full path
        const folderPath = generateFolderPath();
        processedKey = `live/${folderPath}${fileKey}`;
      } else {
        // Use fileKey as-is and let backend handle it
        processedKey = fileKey;
      }

      console.log('File key processing:', { original: fileKey, processed: processedKey });

      // Use the backend API to get presigned URL
      const result = await s3Service.getPresignedUrl(processedKey, expiresIn);
      
      if (result.success) {
        return { 
          success: true, 
          data: { url: result.data.url || result.data.presignedUrl || result.data },
          message: 'File URL generated successfully'
        };
      } else {
        return { success: false, error: result.error || 'Failed to generate file URL' };
      }
    } catch (error) {
      return { success: false, error: error.message || 'Failed to get file URL' };
    }
  }, [orgId, section, generateFolderPath]);

  // Remove file reference locally (doesn't delete from S3)
  const removeFileReference = useCallback(async (fieldName) => {
    try {
      // Call backend API to remove reference from database
      const result = await s3Service.removeFileReference(orgId, fieldName);
      
      if (result.success) {
        // Also remove from local state
        setUploadedFiles(prev => {
          const updated = { ...prev };
          delete updated[fieldName];
          return updated;
        });
        
        // Clear any progress/errors
        setUploadProgress(prev => {
          const updated = { ...prev };
          delete updated[fieldName];
          return updated;
        });
        
        setUploadErrors(prev => {
          const updated = { ...prev };
          delete updated[fieldName];
          return updated;
        });
      }

      return result;
    } catch (error) {
      console.error('Remove file reference error:', error);
      return { success: false, error: error.message };
    }
  }, [orgId]);

  // Remove file reference locally only (no API call)
  const removeFileReferenceLocal = useCallback((fieldName) => {
    try {
      // Remove from local state only
      setUploadedFiles(prev => {
        const updated = { ...prev };
        delete updated[fieldName];
        return updated;
      });
      
      // Clear any progress/errors
      setUploadProgress(prev => {
        const updated = { ...prev };
        delete updated[fieldName];
        return updated;
      });
      
      setUploadErrors(prev => {
        const updated = { ...prev };
        delete updated[fieldName];
        return updated;
      });

      return { success: true, message: 'File reference removed locally' };
    } catch (error) {
      console.error('Remove file reference locally error:', error);
      return { success: false, error: error.message };
    }
  }, []);

  return {
    // Upload functions
    uploadFile,
    uploadMultipleFiles,
    deleteFile,
    removeFileReference,
    removeFileReferenceLocal,
    getFileUrl,
    getFileUrlByKey,
    
    // State management
    clearUploadState,
    getUploadInfo,
    hasActiveUploads,
    
    // State values
    uploadProgress,
    uploadErrors,
    uploadedFiles,
    isUploading,
    
    // Utilities
    generateFolderPath
  };
};

export default useFileUpload;
